// Admin → Feature flags. Toggle platform features on/off. Stored in
// site_settings (feature_flags). docs/replan-v2.md (admin console, slice 4).

import { Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { deleteFlag, listFlags, upsertFlag, type FeatureFlag } from '~/lib/admin';
import { ApiError } from '~/lib/api';

export const Route = createFileRoute('/_authed/flags')({
  component: FlagsPage,
});

function FlagsPage() {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ['flags'], queryFn: ({ signal }) => listFlags(signal) });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['flags'] });

  const create = useMutation({
    mutationFn: () =>
      upsertFlag({ name: name.trim(), enabled: false, description: description.trim() }),
    onSuccess: () => {
      setName('');
      setDescription('');
      void invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Something went wrong.'),
  });
  const toggle = useMutation({
    mutationFn: (f: FeatureFlag) => upsertFlag({ ...f, enabled: !f.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (n: string) => deleteFlag(n), onSuccess: invalidate });

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-semibold text-admin-900">Feature flags</h1>
      <p className="mt-1 text-sm text-ink-700">Toggle platform features without a redeploy.</p>

      <form
        className="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-burgundy-500/10 bg-white p-5"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (name.trim()) create.mutate();
        }}
      >
        <label className="block text-sm font-medium text-ink-700">
          Flag name
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ai_tutor.enabled"
          />
        </label>
        <label className="block flex-1 text-sm font-medium text-ink-700">
          Description
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What it gates"
          />
        </label>
        <Button type="submit" disabled={create.isPending || name.trim() === ''}>
          Add flag
        </Button>
      </form>
      {error && <p className="mt-2 text-sm text-heart">{error}</p>}

      <div className="mt-6 space-y-2">
        {flags.isLoading ? (
          <p className="text-sm text-ink-500">Loading…</p>
        ) : (flags.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-500">No flags yet.</p>
        ) : (
          flags.data!.map((f) => (
            <div
              key={f.name}
              className="flex items-center justify-between gap-4 rounded-2xl border border-burgundy-500/10 bg-white p-4"
            >
              <div className="min-w-0">
                <p className="font-mono font-medium text-ink-900">{f.name}</p>
                {f.description && <p className="text-sm text-ink-500">{f.description}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggle.mutate(f)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    f.enabled ? 'bg-leaf' : 'bg-cream-200'
                  }`}
                  aria-pressed={f.enabled}
                  aria-label={`Toggle ${f.name}`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                      f.enabled ? 'start-[1.375rem]' : 'start-0.5'
                    }`}
                  />
                </button>
                <Button variant="ghost" size="sm" onClick={() => remove.mutate(f.name)}>
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
